import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import { useAdmin } from '../../hooks/useAdmin';
import { getCurrentTier, tiers } from '../../constants/levels';
import { AVAILABLE_LANGUAGES, GAME_GENRES, PLATFORMS } from '../../constants/profile';
import '../../admin.css';

const TIER_ORDER = ['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master'];

const PLATFORM_COLORS = {
  pc: '#B2F048',
  ps: '#4A90D9',
  xbox: '#6CC644',
  switch: '#E06C75',
  mobile: '#FFB84D',
  vr: '#B9F2FF',
  retro: '#C0C0C0',
};

const COMPLETENESS_FIELDS = [
  { key: 'languages', label: 'Languages', check: u => u.streamingLanguages?.primary?.length > 0 },
  { key: 'genres', label: 'Genres', check: u => u.genres?.length > 0 },
  { key: 'platforms', label: 'Platforms', check: u => u.platforms?.length > 0 },
  { key: 'region', label: 'Region', check: u => !!u.region?.continent },
  { key: 'bio', label: 'Bio', check: u => !!u.bio?.trim() },
  { key: 'social', label: 'Social Links', check: u => !!u.socialLinks?.twitch || !!u.socialLinks?.customLink?.link },
];

function codeName(list, code) {
  const item = list.find(l => l.code === code);
  return item ? item.name : code;
}

function sortedEntries(obj) {
  return Object.entries(obj).sort((a, b) => b[1] - a[1]);
}

export default function AdminStats({ users = [], availableQuests = [], usersLoading, questsLoading }) {
  const { t } = useTranslation();
  const { getUserStats } = useAdmin();
  const [animateIn, setAnimateIn] = useState(false);
  const [showAllLanguages, setShowAllLanguages] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 60);
    return () => clearTimeout(timer);
  }, []);

  const overallStats = useMemo(() => {
    let totalXP = 0;
    let totalCompleted = 0;
    let totalFailed = 0;
    let totalActive = 0;
    let activeUsers = 0;

    users.forEach(user => {
      const stats = getUserStats(user);
      totalXP += stats.totalXP;
      totalCompleted += stats.completedCount;
      totalFailed += stats.failedCount;
      totalActive += stats.activeCount;
      if (stats.completedCount > 0 || stats.activeCount > 0) {
        activeUsers++;
      }
    });

    const totalAttempts = totalCompleted + totalFailed;
    const overallSuccessRate = totalAttempts > 0 ? Math.round((totalCompleted / totalAttempts) * 100) : 0;

    return {
      totalUsers: users.length,
      activeUsers,
      totalXP,
      totalCompleted,
      totalFailed,
      totalActive,
      overallSuccessRate,
      averageXPPerUser: users.length > 0 ? Math.round(totalXP / users.length) : 0
    };
  }, [users, getUserStats]);

  const questStats = useMemo(() => {
    const activeQuests = availableQuests.filter(q => q.isActive !== false).length;
    const inactiveQuests = availableQuests.length - activeQuests;


    return {
      totalQuests: availableQuests.length,
      activeQuests,
      inactiveQuests,
      averageXP: availableQuests.length > 0
        ? Math.round(availableQuests.reduce((sum, q) => sum + (q.value || 0), 0) / availableQuests.length)
        : 0
    };
  }, [availableQuests]);

  // Community stats — single pass over users
  const communityStats = useMemo(() => {
    const tierCounts = {};
    TIER_ORDER.forEach(t => tierCounts[t] = 0);

    const languages = {};
    const genres = {};
    const platforms = {};
    const regions = {};
    let matureCount = 0;
    let allAudiencesCount = 0;
    const profileFields = {};
    COMPLETENESS_FIELDS.forEach(f => profileFields[f.key] = 0);

    users.forEach(user => {
      const stats = getUserStats(user);
      const tier = getCurrentTier(stats.totalXP);
      tierCounts[tier] = (tierCounts[tier] || 0) + 1;

      (user.streamingLanguages?.primary || []).forEach(code => {
        languages[code] = (languages[code] || 0) + 1;
      });

      (user.genres || []).forEach(code => {
        genres[code] = (genres[code] || 0) + 1;
      });

      (user.platforms || []).forEach(code => {
        platforms[code] = (platforms[code] || 0) + 1;
      });

      if (user.region?.continent) {
        regions[user.region.continent] = (regions[user.region.continent] || 0) + 1;
      }

      if (user.matureContent === 'mature-18') {
        matureCount++;
      } else {
        allAudiencesCount++;
      }

      COMPLETENESS_FIELDS.forEach(f => {
        if (f.check(user)) profileFields[f.key]++;
      });
    });

    const total = users.length || 1;

    return {
      tierCounts,
      languages: sortedEntries(languages),
      genres: sortedEntries(genres),
      platforms: sortedEntries(platforms),
      regions: sortedEntries(regions),
      contentRating: { mature: matureCount, allAudiences: allAudiencesCount },
      profileCompleteness: COMPLETENESS_FIELDS.map(f => ({
        label: f.label,
        pct: Math.round((profileFields[f.key] / total) * 100),
      })),
    };
  }, [users, getUserStats]);

  if (usersLoading || questsLoading) {
    return (
      <div className="admin-stats-loading">
        <FontAwesomeIcon icon={ICONS.SPINNER} spin />
        <p>{t('admin.stats.loadingStatistics')}</p>
      </div>
    );
  }


  // Donut gradient
  const totalUsers = users.length || 1;
  const tierSegments = TIER_ORDER.map(tier => ({
    tier,
    count: communityStats.tierCounts[tier],
    color: tiers[tier].color,
    pct: (communityStats.tierCounts[tier] / totalUsers) * 100,
  })).filter(s => s.count > 0);

  let cumulativeDeg = 0;
  const gradientStops = tierSegments.flatMap(s => {
    const startDeg = cumulativeDeg;
    const endDeg = cumulativeDeg + (s.pct / 100) * 360;
    cumulativeDeg = endDeg;
    return [`${s.color} ${startDeg}deg ${endDeg}deg`];
  });
  const donutGradient = gradientStops.length > 0
    ? `conic-gradient(${gradientStops.join(', ')})`
    : 'conic-gradient(var(--cBackgroundEl3) 0deg 360deg)';

  // Histogram max
  // Distribution helpers
  const maxLangCount = communityStats.languages.length > 0 ? communityStats.languages[0][1] : 1;
  const maxGenreCount = communityStats.genres.length > 0 ? communityStats.genres[0][1] : 1;
  const maxRegionCount = communityStats.regions.length > 0 ? communityStats.regions[0][1] : 1;

  const visibleLanguages = showAllLanguages ? communityStats.languages : communityStats.languages.slice(0, 5);
  const hasMoreLanguages = communityStats.languages.length > 5;

  const totalPlatforms = communityStats.platforms.reduce((sum, [, c]) => sum + c, 0) || 1;

  const contentTotal = communityStats.contentRating.mature + communityStats.contentRating.allAudiences || 1;
  const allAudPct = (communityStats.contentRating.allAudiences / contentTotal) * 100;
  const maturePct = (communityStats.contentRating.mature / contentTotal) * 100;

  return (
    <div className="admin-stats">
      <h2>{t('admin.stats.overviewAndStatistics')}</h2>

      {/* User Statistics - inline strip */}
      <div className="user-stats-strip">
        <div className="user-stats-item">
          <FontAwesomeIcon icon={ICONS.USERS} className="user-stats-icon" />
          <span className="user-stats-value">{overallStats.totalUsers}</span>
          <span className="user-stats-label">{t('admin.stats.totalUsers')}</span>
        </div>
        <div className="user-stats-divider" />
        <div className="user-stats-item">
          <FontAwesomeIcon icon={ICONS.USER_CIRCLE} className="user-stats-icon accent" />
          <span className="user-stats-value accent">{overallStats.activeUsers}</span>
          <span className="user-stats-label">{t('admin.stats.activeUsers')}</span>
        </div>
        <div className="user-stats-divider" />
        <div className="user-stats-item">
          <FontAwesomeIcon icon={ICONS.STAR} className="user-stats-icon accent" />
          <span className="user-stats-value accent">{overallStats.totalXP.toLocaleString()}</span>
          <span className="user-stats-label">{t('admin.stats.totalXPEarned')}</span>
        </div>
        <div className="user-stats-divider" />
        <div className="user-stats-item">
          <FontAwesomeIcon icon={ICONS.CHART_LINE} className="user-stats-icon" />
          <span className="user-stats-value">{overallStats.averageXPPerUser}</span>
          <span className="user-stats-label">{t('admin.stats.avgXPPerUser')}</span>
        </div>
      </div>

      {/* Quests - Unified Panel */}
      <section className="overview-section">
        <h3 className="overview-section-title">
          <FontAwesomeIcon icon={ICONS.CHART_PIE} />
          Community Progression
        </h3>
        <div className="overview-grid overview-grid-2">
          {/* Tier Donut - Left */}
          <div className="cs-insight-card">
            <h4 className="cs-card-title">Tier Distribution</h4>
            <div className="cs-donut-wrap">
              <div
                className="cs-donut"
                style={{
                  background: donutGradient,
                  opacity: animateIn ? 1 : 0,
                  transform: animateIn ? 'scale(1)' : 'scale(0.85)',
                }}
              >
                <div className="cs-donut-hole">
                  <span className="cs-donut-value">{users.length}</span>
                  <span className="cs-donut-label">Users</span>
                </div>
              </div>
            </div>
            <div className="cs-legend">
              {TIER_ORDER.map(tier => (
                <div key={tier} className="cs-legend-item">
                  <span className="cs-legend-dot" style={{ background: tiers[tier].color }}></span>
                  <span className="cs-legend-name">{tiers[tier].name}</span>
                  <span className="cs-legend-count">{communityStats.tierCounts[tier]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quest Stats - Right */}
          <div className="cs-insight-card quest-stats-compact">
            <h4 className="cs-card-title">{t('admin.stats.questBreakdown')}</h4>
            <div className="quest-stats-grid">
              <div className="quest-stat-item">
                <span className="quest-stat-value">{questStats.totalQuests}</span>
                <span className="quest-stat-label">{t('admin.stats.totalQuests')}</span>
              </div>
              <div className="quest-stat-item">
                <span className="quest-stat-value qp-accent">{questStats.activeQuests}</span>
                <span className="quest-stat-label">{t('admin.stats.activeQuests')}</span>
              </div>
              <div className="quest-stat-item">
                <span className="quest-stat-value qp-muted">{questStats.inactiveQuests}</span>
                <span className="quest-stat-label">{t('admin.stats.inactiveQuests')}</span>
              </div>
              <div className="quest-stat-item">
                <span className="quest-stat-value qp-accent">{overallStats.totalCompleted.toLocaleString()}</span>
                <span className="quest-stat-label">{t('admin.stats.questsCompleted')}</span>
              </div>
              <div className="quest-stat-item">
                <span className="quest-stat-value qp-muted">{overallStats.totalFailed}</span>
                <span className="quest-stat-label">{t('admin.stats.questsFailed')}</span>
              </div>
              <div className="quest-stat-item">
                <span className="quest-stat-value">{overallStats.overallSuccessRate}%</span>
                <span className="quest-stat-label">{t('admin.stats.overallSuccessRate')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Audience Insights ===== */}
      <section className="overview-section">
        <h3 className="overview-section-title">
          <FontAwesomeIcon icon={ICONS.GLOBE} />
          Audience Insights
        </h3>
        <div className="overview-grid overview-grid-2">
          {/* Languages */}
          <div className="cs-insight-card">
            <h4 className="cs-card-title">Languages</h4>
            <div className="cs-dist-list">
              {visibleLanguages.map(([code, count]) => (
                <div key={code} className="qp-dist-row">
                  <span className="qp-dist-dot" style={{ background: 'var(--cPrimary)' }}></span>
                  <span className="qp-dist-label">{codeName(AVAILABLE_LANGUAGES, code)}</span>
                  <span className="qp-dist-count">{count}</span>
                  <div className="qp-dist-track">
                    <div
                      className="qp-dist-fill"
                      style={{
                        width: animateIn ? `${(count / maxLangCount) * 100}%` : '0%',
                        background: 'var(--cPrimary)',
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            {hasMoreLanguages && (
              <button
                className="cs-expand-btn"
                onClick={() => setShowAllLanguages(v => !v)}
              >
                {showAllLanguages ? 'Show less' : `Show all ${communityStats.languages.length} languages`}
                <FontAwesomeIcon icon={showAllLanguages ? ICONS.CHEVRON_UP : ICONS.CHEVRON_DOWN} />
              </button>
            )}
          </div>

          {/* Genres */}
          <div className="cs-insight-card">
            <h4 className="cs-card-title">Genres</h4>
            <div className="cs-dist-list">
              {communityStats.genres.map(([code, count]) => (
                <div key={code} className="qp-dist-row">
                  <span className="qp-dist-dot" style={{ background: 'var(--color-warning)' }}></span>
                  <span className="qp-dist-label">{codeName(GAME_GENRES, code)}</span>
                  <span className="qp-dist-count">{count}</span>
                  <div className="qp-dist-track">
                    <div
                      className="qp-dist-fill"
                      style={{
                        width: animateIn ? `${(count / maxGenreCount) * 100}%` : '0%',
                        background: 'var(--color-warning)',
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Platforms */}
          <div className="cs-insight-card">
            <h4 className="cs-card-title">Platforms</h4>
            <div className="cs-seg-bar">
              {communityStats.platforms.map(([code, count]) => (
                <div
                  key={code}
                  className="cs-seg-bar-segment"
                  style={{
                    flexBasis: animateIn ? `${(count / totalPlatforms) * 100}%` : '0%',
                    background: PLATFORM_COLORS[code] || 'var(--cPrimary)',
                  }}
                ></div>
              ))}
            </div>
            <div className="cs-seg-legend">
              {communityStats.platforms.map(([code, count]) => (
                <div key={code} className="cs-seg-legend-item">
                  <span className="cs-seg-legend-color" style={{ background: PLATFORM_COLORS[code] || 'var(--cPrimary)' }}></span>
                  <span className="cs-seg-legend-name">{codeName(PLATFORMS, code)}</span>
                  <span className="cs-seg-legend-count">{count}</span>
                  <span className="cs-seg-legend-pct">{Math.round((count / totalPlatforms) * 100)}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Regions */}
          <div className="cs-insight-card">
            <h4 className="cs-card-title">Regions</h4>
            <div className="cs-dist-list">
              {communityStats.regions.map(([continent, count]) => (
                <div key={continent} className="qp-dist-row">
                  <span className="qp-dist-dot" style={{ background: '#4A90D9' }}></span>
                  <span className="qp-dist-label">{continent}</span>
                  <span className="qp-dist-count">{count}</span>
                  <div className="qp-dist-track">
                    <div
                      className="qp-dist-fill"
                      style={{
                        width: animateIn ? `${(count / maxRegionCount) * 100}%` : '0%',
                        background: '#4A90D9',
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Community Health ===== */}
      <section className="overview-section">
        <h3 className="overview-section-title">
          <FontAwesomeIcon icon={ICONS.SHIELD} />
          Community Health
        </h3>
        <div className="overview-grid overview-grid-2">
          {/* Content Rating */}
          <div className="cs-insight-card">
            <h4 className="cs-card-title">Content Rating</h4>
            <div className="cs-seg-bar cs-seg-bar-lg">
              <div
                className="cs-seg-bar-segment"
                style={{
                  flexBasis: animateIn ? `${allAudPct}%` : '0%',
                  background: 'var(--cPrimary)',
                }}
              ></div>
              <div
                className="cs-seg-bar-segment"
                style={{
                  flexBasis: animateIn ? `${maturePct}%` : '0%',
                  background: 'var(--color-error)',
                }}
              ></div>
            </div>
            <div className="cs-rating-stats">
              <div className="cs-rating-stat">
                <span className="cs-rating-dot" style={{ background: 'var(--cPrimary)' }}></span>
                <span className="cs-rating-label">All Audiences</span>
                <span className="cs-rating-value">{communityStats.contentRating.allAudiences}</span>
                <span className="cs-rating-pct">{Math.round(allAudPct)}%</span>
              </div>
              <div className="cs-rating-stat">
                <span className="cs-rating-dot" style={{ background: 'var(--color-error)' }}></span>
                <span className="cs-rating-label">Mature 18+</span>
                <span className="cs-rating-value">{communityStats.contentRating.mature}</span>
                <span className="cs-rating-pct">{Math.round(maturePct)}%</span>
              </div>
            </div>
          </div>

          {/* Profile Completeness */}
          <div className="cs-insight-card">
            <h4 className="cs-card-title">Profile Completeness</h4>
            <div className="cs-completeness-list">
              {communityStats.profileCompleteness.map(({ label, pct }) => (
                <div key={label} className="cs-completeness-row">
                  <span className="cs-completeness-label">{label}</span>
                  <span className="cs-completeness-pct">{pct}%</span>
                  <div className="cs-completeness-track">
                    <div
                      className={`cs-completeness-fill ${pct >= 70 ? 'high' : pct >= 30 ? 'mid' : 'low'}`}
                      style={{ width: animateIn ? `${pct}%` : '0%' }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
